import { useAlbums } from '@/stores/useAlbums'
import { useQuery } from '@tanstack/react-query'
import { Outlet, useParams, useSearchParams } from 'react-router-dom'
import { useEffect } from 'react'
import { useApi } from '@/stores/useApi'
import LoadBig from '@/components/LoadBig/LoadBig'
import { useDrives } from '@/stores/useDrives.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { HeyProfile } from '@/models/profile.ts'
import { getThemeDefinitionForDrive, HeyDrive } from '@/models/drive.ts'
import { useHeyTheme } from '@/contexts/heyTheme.tsx'
import { getDriveDefaultsFor } from '@/controller/driveDefaults.ts'
import DemoHelloModal from '@/components/DemoHelloModal.tsx'
import { getFontDuoForFontable } from '@/models/fontDetails.ts'

export function DrivePage() {
  return (
    <>
      <DrivePageContent />
    </>
  );
}

function DrivePageContent() {
  const { driveId } = useParams<{ driveId: string }>()
  const [searchParams] = useSearchParams()
  const isPreview = searchParams.get('_preview') === '1'
  const { setAlbum } = useAlbums()
  const { drives, setDrive, setCurrentDrive } = useDrives()
  const { setProfile, authenticationToken, setAuthenticationToken } = useProfile()

  const drive = drives[driveId!]
  const api = useApi()
  const { style: driveStyle, setTheme, color_primary } = useHeyTheme()

  useEffect(() => {
    if (!drive) {
      return
    }

    if (!(drive.color_primary && drive.color_neutral && drive.color_content)) {
      const driveDefaults = getDriveDefaultsFor(drive.drive_type)
      setTheme(
        driveDefaults.color_primary,
        driveDefaults.color_neutral,
        driveDefaults.color_content,
      )
      return
    }

    const fontDuo = getFontDuoForFontable(drive, drive.theme_name, drive.drive_type)
    const themeDef = getThemeDefinitionForDrive(drive)

    console.log()

    setTheme(
      drive.color_primary,
      drive.color_neutral,
      drive.color_content,
      fontDuo.font.css,
      fontDuo.font_content.css,
      fontDuo.font.multiplier,
      fontDuo.font_content.multiplier,
      themeDef?.button_shape ?? null,
      themeDef?.button_variant === 'outline' ? 'outline' : null,
    )
  }, [setTheme, drive?.color_primary, drive?.color_neutral, drive?.color_content, drive?.font, drive?.font_content, drive?.theme_name]);

  const { data: driveData, isLoading } = useQuery({
    queryKey: ['drive', driveId],
    queryFn: () => api.get(`api/drive/${driveId}`),
  })

  const { data: dataProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      try {
        return await api.get(`api/profile/mine`)
      } catch (e) {
        // @ts-ignore
        if (e && e && e.response && e.response.status === 401) {
          setAuthenticationToken(null)
          return null
        }

        throw e
      }
    },
    enabled: authenticationToken != null,
  })

  useEffect(() => {
    if (driveData) {
      console.log('data', driveData)
      const fetchedDrive: HeyDrive = driveData.data

      // In preview mode, override drive values from query params
      if (isPreview) {
        const overrides: Partial<HeyDrive> = {}
        if (searchParams.get('theme_name') != null) overrides.theme_name = searchParams.get('theme_name')!
        if (searchParams.get('color_primary')) overrides.color_primary = searchParams.get('color_primary')!
        if (searchParams.get('color_neutral')) overrides.color_neutral = searchParams.get('color_neutral')!
        if (searchParams.get('color_content')) overrides.color_content = searchParams.get('color_content')!
        if (searchParams.get('spacing')) overrides.spacing = Number(searchParams.get('spacing'))
        if (searchParams.get('layout_mode')) overrides.layout_mode = searchParams.get('layout_mode') as any
        if (searchParams.get('album_overview')) overrides.album_overview = searchParams.get('album_overview') as any
        if (searchParams.get('album_overview_style')) overrides.album_overview_style = searchParams.get('album_overview_style') as any
        if (searchParams.has('are_elements_contained')) overrides.are_elements_contained = searchParams.get('are_elements_contained') === 'true'
        if (searchParams.get('font')) overrides.font = searchParams.get('font')!
        if (searchParams.get('font_content')) overrides.font_content = searchParams.get('font_content')!
        const previewDrive = { ...fetchedDrive, ...overrides }
        setDrive(previewDrive)
        setCurrentDrive(previewDrive)
        for (const album of previewDrive.albums) {
          setAlbum(album)
        }
      } else {
        setDrive(fetchedDrive)
        setCurrentDrive(fetchedDrive)
        for (const album of fetchedDrive.albums) {
          setAlbum(album)
        }
      }
    }
  }, [driveData, setAlbum, setCurrentDrive, setDrive, isPreview, searchParams]);

  useEffect(() => {
    if (dataProfile) {
      console.log('dataProfile', dataProfile)
      const fetchedProfile: HeyProfile = dataProfile.data
      setProfile(fetchedProfile)
    }
  }, [dataProfile, setProfile]);


  if (isLoading || !drive) {
    return <LoadBig />
  }

  return <>
    { color_primary && <meta name='theme-color' content={`#${color_primary}`} /> }
    <style> {driveStyle} </style>
    <Outlet/>
    <DemoHelloModal />
  </>
}
